import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '@/App';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  MessageSquare,
  Mail,
  Bot,
  Clock,
  CheckCircle,
  FileText,
  ShieldCheck,
  Archive,
  RefreshCcw,
  Pencil,
  Plus
} from 'lucide-react';

const HELP_STORAGE_KEY = 'admin_help_items';
const SETTINGS_STORAGE_KEY = 'support_settings';

const defaultHelpItems = [
  {
    id: 'article-1',
    title: 'Getting Started',
    content: 'Welcome to the Help Center. Outline onboarding steps, approvals, and demo flows here.',
    status: 'approved',
    type: 'article',
    category: 'getting_started',
    created_at: new Date().toISOString()
  },
  {
    id: 'article-2',
    title: 'Billing FAQs',
    content: 'Answers for billing, invoices, and launch pricing.',
    status: 'review',
    type: 'article',
    category: 'billing',
    created_at: new Date().toISOString()
  },
  {
    id: 'faq-1',
    title: 'How do I submit content?',
    content: 'Use CREATE → Assisted Autopilot to submit links or uploads.',
    status: 'approved',
    type: 'faq',
    category: 'uploading',
    created_at: new Date().toISOString()
  },
  {
    id: 'faq-2',
    title: 'What is the Self-Marketing Engine?',
    content: 'Internal-only marketing drafts based on the selected App Profile.',
    status: 'draft',
    type: 'faq',
    category: 'self_marketing',
    created_at: new Date().toISOString()
  }
];

const defaultSettings = {
  routingRules: '',
  aiGuardrails: ''
};

function statusBadgeClasses(status) {
  const map = {
    draft: 'bg-gray-500/10 text-gray-300 border-gray-500/30',
    review: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    approved: 'bg-green-500/10 text-green-400 border-green-500/30',
    archived: 'bg-red-500/10 text-red-400 border-red-500/30'
  };
  return map[status] || map.draft;
}

export default function AdminSupport() {
  const { user } = useContext(AuthContext);
  const [helpItems, setHelpItems] = useState([]);
  const [supportSettings, setSupportSettings] = useState(defaultSettings);
  const [supportEmail, setSupportEmail] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [metadata, setMetadata] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [settingsEditor, setSettingsEditor] = useState(null);

  // Admin-only guard
  if (!user?.is_internal_admin && user?.role !== 'admin') {
    return <Navigate to="/app/admin" replace />;
  }

  useEffect(() => {
    const savedItems = localStorage.getItem(HELP_STORAGE_KEY);
    const parsedItems = savedItems ? JSON.parse(savedItems) : defaultHelpItems;
    setHelpItems(parsedItems);

    const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);
    const parsedSettings = savedSettings ? JSON.parse(savedSettings) : defaultSettings;
    setSupportSettings(parsedSettings);

    const savedEmail = localStorage.getItem('support_email');
    const savedMetadata = localStorage.getItem('support_metadata');
    if (savedEmail) {
      setSupportEmail(savedEmail);
      setConnectionStatus('Connected');
    }
    if (savedMetadata) {
      setMetadata(savedMetadata);
    }
  }, []);

  const persistHelpItems = (items) => {
    setHelpItems(items);
    localStorage.setItem(HELP_STORAGE_KEY, JSON.stringify(items));
  };

  const persistSettings = (settings) => {
    setSupportSettings(settings);
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  };

  const statusCounts = useMemo(() => {
    return ['draft', 'review', 'approved', 'archived'].reduce((acc, status) => {
      acc[status] = helpItems.filter((item) => item.status === status).length;
      return acc;
    }, {});
  }, [helpItems]);

  const openItemDetail = (item) => {
    setSelectedItem({ ...item });
    setDetailOpen(true);
  };

  const createItem = (type) => {
    const newItem = {
      id: `${type}-${Date.now()}`,
      title: `New ${type === 'faq' ? 'FAQ' : 'Article'}`,
      content: '',
      status: 'draft',
      type,
      category: 'general',
      created_at: new Date().toISOString()
    };
    persistHelpItems([newItem, ...helpItems]);
    openItemDetail(newItem);
  };

  const saveSelectedItem = () => {
    if (!selectedItem) return;
    const updated = helpItems.map((item) => (item.id === selectedItem.id ? selectedItem : item));
    persistHelpItems(updated);
    setDetailOpen(false);
  };

  const updateStatus = (status) => {
    if (!selectedItem) return;
    const updatedItem = { ...selectedItem, status };
    setSelectedItem(updatedItem);
    const updated = helpItems.map((item) => (item.id === updatedItem.id ? updatedItem : item));
    persistHelpItems(updated);
  };

  const restoreArchived = () => updateStatus('draft');

  const saveSupport = () => {
    localStorage.setItem('support_email', supportEmail);
    localStorage.setItem('support_metadata', metadata);
    setConnectionStatus(supportEmail ? 'Connected' : 'Disconnected');
  };

  const filteredItemsByType = (type) => helpItems.filter((item) => item.type === type);

  const openSettingsEditor = (key) => {
    setSettingsEditor(key);
  };

  const saveSettingsEditor = () => {
    persistSettings({ ...supportSettings });
    setSettingsEditor(null);
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Support Management</h1>
        <p className="text-[#8A93A6]">Closed-loop support system with AI Community Manager</p>
      </div>

      {/* AI Community Manager Notice */}
      <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
        <Bot className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-400 text-sm">
          <strong>Closed-Loop Structure:</strong> Support Overview shows tickets + metrics (read-only). Ticket Management enables threaded, assistive responses. AI Community Manager settings control thresholds and escalation.
        </AlertDescription>
      </Alert>

      {/* Support Overview - Ticket Stats */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white mb-4">Support Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Open Tickets
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">0</div>
              <p className="text-xs text-[#8A93A6] mt-1">Awaiting response</p>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Avg Response Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">--</div>
              <p className="text-xs text-[#8A93A6] mt-1">Minutes</p>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Resolved Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">0</div>
              <p className="text-xs text-[#8A93A6] mt-1">Tickets closed</p>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Bot className="h-4 w-4" />
                AI Handled
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">--%</div>
              <p className="text-xs text-[#8A93A6] mt-1">Auto-resolved</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Support Inbox + Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-blue-500" />
              Support Inbox / Email
            </CardTitle>
            <CardDescription>Save support email and view connection state</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              type="email"
              placeholder="support@yourdomain.com"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              className="bg-[#1C1F26] border-white/10 text-white"
            />
            <Textarea
              placeholder="Support metadata, routing notes, SLAs..."
              value={metadata}
              onChange={(e) => setMetadata(e.target.value)}
              className="bg-[#1C1F26] border-white/10 text-white"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#8A93A6]">Connection State: {connectionStatus}</p>
              <Button onClick={saveSupport}>Save Settings</Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 gap-4">
          <Card className="glass cursor-pointer" onClick={() => openSettingsEditor('routingRules')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-pink-500" />
                Routing Rules
              </CardTitle>
              <CardDescription>Edit routing rules and persist settings</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#8A93A6] line-clamp-2">{supportSettings.routingRules || 'Click to edit routing rules'}</p>
            </CardContent>
          </Card>

          <Card className="glass cursor-pointer" onClick={() => openSettingsEditor('aiGuardrails')}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-green-500" />
                AI Guardrails
              </CardTitle>
              <CardDescription>Edit AI guardrails and persist settings</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#8A93A6] line-clamp-2">{supportSettings.aiGuardrails || 'Click to edit AI guardrails'}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Ticket Management */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle>Ticket Management (Threaded, Assistive)</CardTitle>
          <CardDescription>Manage user support requests with AI assistance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Mail className="h-12 w-12 text-[#8A93A6] mx-auto mb-4" />
            <p className="text-[#8A93A6]">No support tickets yet</p>
            <p className="text-sm text-[#8A93A6] mt-2">Connect your inbox to start receiving and managing tickets</p>
          </div>
        </CardContent>
      </Card>

      {/* Help Center Governance */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Articles</CardTitle>
                <CardDescription>Draft → Review → Approved → Archived</CardDescription>
              </div>
              <Button size="sm" onClick={() => createItem('article')}>
                <Plus className="h-4 w-4 mr-1" /> New Article
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {filteredItemsByType('article').map((article) => (
              <div
                key={article.id}
                className="p-3 rounded-lg bg-white/5 border border-white/10 hover:border-blue-500/30 cursor-pointer"
                onClick={() => openItemDetail(article)}
              >
                <div className="flex items-center justify-between">
                  <p className="text-white font-medium">{article.title}</p>
                  <span className={`text-xs px-2 py-1 rounded border ${statusBadgeClasses(article.status)}`}>
                    {article.status}
                  </span>
                </div>
                <p className="text-xs text-[#8A93A6] mt-1">{article.type.toUpperCase()} • {new Date(article.created_at).toLocaleDateString()}</p>
              </div>
            ))}
            {filteredItemsByType('article').length === 0 && (
              <p className="text-sm text-[#8A93A6] text-center py-6">No articles yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>FAQs</CardTitle>
                <CardDescription>Same governance as articles</CardDescription>
              </div>
              <Button size="sm" onClick={() => createItem('faq')}>
                <Plus className="h-4 w-4 mr-1" /> New FAQ
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {filteredItemsByType('faq').map((faq) => (
              <div
                key={faq.id}
                className="p-3 rounded-lg bg-white/5 border border-white/10 hover:border-blue-500/30 cursor-pointer"
                onClick={() => openItemDetail(faq)}
              >
                <div className="flex items-center justify-between">
                  <p className="text-white font-medium">{faq.title}</p>
                  <span className={`text-xs px-2 py-1 rounded border ${statusBadgeClasses(faq.status)}`}>
                    {faq.status}
                  </span>
                </div>
                <p className="text-xs text-[#8A93A6] mt-1">{faq.type.toUpperCase()} • {new Date(faq.created_at).toLocaleDateString()}</p>
              </div>
            ))}
            {filteredItemsByType('faq').length === 0 && (
              <p className="text-sm text-[#8A93A6] text-center py-6">No FAQs yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Status summary */}
      <Card className="glass mt-6">
        <CardHeader>
          <CardTitle>Help Center Workflow Status</CardTitle>
          <CardDescription>Draft → Review → Approved → Archived</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['draft', 'review', 'approved', 'archived'].map((status) => (
              <div key={status} className="p-3 rounded-lg bg-white/5 border border-white/10">
                <p className="text-sm text-white capitalize">{status}</p>
                <p className="text-2xl font-bold text-white">{statusCounts[status] || 0}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {detailOpen && selectedItem && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="w-full max-w-2xl bg-[#0F1218] border border-white/10 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-[#8A93A6] uppercase">{selectedItem.type === 'faq' ? 'FAQ' : 'Article'} Detail</p>
                <h3 className="text-2xl font-bold text-white">{selectedItem.title || 'Untitled'}</h3>
                <p className="text-sm text-[#8A93A6]">Status: {selectedItem.status}</p>
              </div>
              <button
                className="text-[#8A93A6] hover:text-white"
                onClick={() => setDetailOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-[#8A93A6]">Title</label>
                <Input
                  value={selectedItem.title}
                  onChange={(e) => setSelectedItem({ ...selectedItem, title: e.target.value })}
                  className="mt-2"
                />
              </div>

              <div>
                <label className="text-sm text-[#8A93A6]">Content / Body</label>
                <Textarea
                  value={selectedItem.content}
                  onChange={(e) => setSelectedItem({ ...selectedItem, content: e.target.value })}
                  className="mt-2 min-h-[160px]"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => updateStatus('review')}>
                  <Pencil className="h-4 w-4 mr-1" /> Send to Review
                </Button>
                <Button onClick={() => updateStatus('approved')}>
                  <CheckCircle className="h-4 w-4 mr-1" /> Approve
                </Button>
                <Button variant="secondary" onClick={() => updateStatus('draft')}>
                  <RefreshCcw className="h-4 w-4 mr-1" /> Reject (Draft)
                </Button>
                <Button variant="destructive" onClick={() => updateStatus('archived')}>
                  <Archive className="h-4 w-4 mr-1" /> Archive
                </Button>
                {selectedItem.status === 'archived' && (
                  <Button variant="outline" onClick={restoreArchived}>
                    <RefreshCcw className="h-4 w-4 mr-1" /> Restore
                  </Button>
                )}
              </div>

              <div className="flex items-center justify-between">
                <p className="text-xs text-[#8A93A6]">Type: {selectedItem.type} • Status: {selectedItem.status}</p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setDetailOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveSelectedItem}>Save</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Editor Modal */}
      {settingsEditor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
          <div className="w-full max-w-xl bg-[#0F1218] border border-white/10 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-[#8A93A6] uppercase">Support Setting</p>
                <h3 className="text-2xl font-bold text-white">
                  {settingsEditor === 'routingRules' ? 'Routing Rules' : 'AI Guardrails'}
                </h3>
              </div>
              <button
                className="text-[#8A93A6] hover:text-white"
                onClick={() => setSettingsEditor(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <Textarea
              value={supportSettings[settingsEditor] || ''}
              onChange={(e) => setSupportSettings({ ...supportSettings, [settingsEditor]: e.target.value })}
              className="min-h-[200px]"
            />

            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setSettingsEditor(null)}>
                Cancel
              </Button>
              <Button onClick={saveSettingsEditor}>Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
